import React, { useState, useEffect, useRef } from 'react';
import { Modal, message, Button, Space } from 'antd';
import { DownloadOutlined, FilePdfOutlined } from '@ant-design/icons';
import CopyToClipboard from 'react-copy-to-clipboard';
import firebase from 'firebase';
import QRCode from 'qrcode';

import TrackerService from '../../utils/TrackerService';
import { getAppBaseUrl } from '../../utils/URLBuilder';
import striketipLogo from '../../images/striketip_black_text_url.png';

// Invite timestamps arrive as Firestore Timestamps from the team doc, but as
// Dates once we generate a fresh code in this session. Older teams have an
// inviteUuid with no timestamps at all — calling .toDate() on those threw, which
// surfaced to the user as "Link copy failed" on a link that was perfectly fine.
const toDate = (value) => (
  !value ? null
    : typeof value.toDate === 'function' ? value.toDate()
      : value instanceof Date ? value
        : null
);

const generateInviteUuid = () => {
  const { v4: uuidv4 } = require('uuid');
  return uuidv4().substring(1, 7).toString();
};

// Share the QR via the native share sheet where available, otherwise download it.
const saveQRImage = async (qrDataUrl, teamName) => {
  if (!qrDataUrl) return;

  let shared = false;
  if (navigator.share && navigator.canShare) {
    try {
      const res = await fetch(qrDataUrl);
      const blob = await res.blob();
      const file = new File([blob], `${teamName || 'team'}-invite-qr.png`, { type: 'image/png' });
      if (navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `${teamName || 'Team'} invite QR code` });
        shared = true;
      }
    } catch { /* fall through to download */ }
  }

  if (!shared) {
    const link = document.createElement('a');
    link.href = qrDataUrl;
    link.download = `${teamName || 'team'}-invite-qr.png`;
    link.click();
  }
};

/**
 * `canManageInvite` — whether the viewer may mint a NEW invite code. Only a team
 * manager or the owner may; everyone else shares the code that already exists.
 * Rotating the code is a team-wide act: it is the one invite link printed on
 * staff-room posters and pasted into group chats, so a member opening this modal
 * must not silently replace it for everybody.
 */
export const TeamMemberInviteModal = ({ visible, teamId, teamName, team, clientId, clientName, canManageInvite, teamMemberInviteUuid, teamMemberInviteCreatedBy, teamMemberInviteCreatedAt, teamMemberInviteExpiresAt, onClickInviteModalOk, onClickModalCancel }) => {

    const urlBase = getAppBaseUrl();

    const instructionBefore = `Copy the link below and send it to anyone (or any group) you want to invite - it can be used to invite as many people as you'd like`;

    const instructionAfter = `The link has been copied to your clipboard.
        Open WhatsApp, SMS or Email app, paste the copied link.
        In WhatsApp send this link to your team group chats.
        The same link works for everyone.`;

    const [messageText, setMessageText] = useState(instructionBefore);
    const [isLinkCopied, setIsLinkCopied] = useState(false);
    const [inviteUuid, setInviteUuid] = useState(teamMemberInviteUuid);
    const [inviteCreatedAt, setInviteCreatedAt] = useState(teamMemberInviteCreatedAt);
    const [inviteExpiresAt, setInviteExpiresAt] = useState(teamMemberInviteExpiresAt);
    const [inviteCreatedBy, setInviteCreatedBy] = useState(teamMemberInviteCreatedBy);
    const [qrDataUrl, setQrDataUrl] = useState(null);
    // True only when this session minted a new code that isn't saved yet. Sharing
    // an existing link writes nothing at all.
    const [needsPersist, setNeedsPersist] = useState(false);

    const inviteUrl = inviteUuid
      ? `${urlBase}/client/manage/account/teamInvites/${teamId}/${inviteUuid}/`
      : null;

    // Mint a code only when the team has none at all. Deliberately NOT on expiry:
    // invite links are not time-limited. Owners routinely took longer than a week
    // to get round to sharing, the code silently rotated underneath them, and the
    // link they had already sent stopped being the current one. Expiry was never
    // enforced on the accept path either, so it only ever caused that churn.
    // Existing teams still carry a stale inviteExpiresAt; it is ignored.
    useEffect(() => {
      const shouldMintCode = visible && !inviteUuid && canManageInvite;
      if (!shouldMintCode) return;

      setInviteUuid(generateInviteUuid());
      setInviteCreatedAt(new Date());
      setInviteExpiresAt(null);
      setInviteCreatedBy(clientId);
      setNeedsPersist(true);
    }, [visible]);

    useEffect(() => {
      if (!inviteUrl) return;
      QRCode.toDataURL(inviteUrl, { errorCorrectionLevel: 'H', margin: 2, width: 256 })
        .then(setQrDataUrl)
        .catch(() => setQrDataUrl(null));
    }, [inviteUrl]);

    const isLocalHost = window.location.hostname === 'localhost';
    const isTest = isLocalHost;

    const tracker = new TrackerService({
      isTest,
    });

    const onCopied = async () => {
      setMessageText(instructionAfter);

      if (needsPersist) {
        try {
          await updateInviteDetails();
          setNeedsPersist(false);
        } catch (err) {
          console.error('Saving the new invite code failed:', err);
          message.error('The invite link could not be saved — ask a team manager to create one', 5);
          return;
        }
      }

      setIsLinkCopied(true);
      message.success('Link copied', 5);

      const createdAtDate = toDate(inviteCreatedAt);
      const expiresAtDate = toDate(inviteExpiresAt);
      const inviteCreatedAtString = createdAtDate ? createdAtDate.toISOString() : null;
      const inviteExpiresAtString = expiresAtDate ? expiresAtDate.toISOString() : null;

      const data = {
        teamId,
        teamName,
        clientId,
        clientName,
        inviteUuid,
        inviteCreatedAt: inviteCreatedAtString,
        inviteExpiresAt: inviteExpiresAtString,
        inviteCreatedBy,
      }

      tracker.mixpanelTrack('invite_team_member', data);
      if (!isTest) {
        tracker.googleTrack('invite_team_member', data);
      }
    };

    const updateInviteDetails = async () => {
      const db = firebase.firestore();
      await db.collection('teams').doc(teamId).update({
        inviteUuid,
        inviteCreatedAt: toDate(inviteCreatedAt),
        inviteExpiresAt: toDate(inviteExpiresAt),
        inviteCreatedBy,
      });
    };


    const handlePrintQR = async () => {
      if (!qrDataUrl) return;

      const toBase64 = async (url) => {
        const res = await fetch(url);
        const blob = await res.blob();
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      };

      let logoSrc = striketipLogo;
      try { logoSrc = await toBase64(striketipLogo); } catch { /* use URL */ }

      const addr = team?.address || {};
      const addrLines = [
        addr['address-line-one'], addr['address-line-two'],
        addr.town, addr.county, addr.zip, addr.country,
      ].filter(Boolean);
      const contact = team?.contact || {};
      const contactLines = [contact.email, contact.phone, contact.mobile].filter(Boolean);
      const contactInfoHtml = (addrLines.length || contactLines.length)
        ? `<div class="contact-info">
            ${addrLines.length ? `<p class="contact-block">${addrLines.join(', ')}</p>` : ''}
            ${contactLines.length ? `<p class="contact-block">${contactLines.join(' &nbsp;·&nbsp; ')}</p>` : ''}
           </div>`
        : '';

      const printWindow = window.open('', '_blank');
      printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>${teamName || 'Team'} — Staff Invite QR</title>
  <style>
    @page { size: A4; margin: 20mm; }
    @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
    * { box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; margin: 0; padding: 0; min-height: 100vh; display: flex; flex-direction: column; }
    .page { max-width: 160mm; width: 100%; margin: 0 auto; padding: 16mm 0 8mm; flex: 1; display: flex; flex-direction: column; align-items: center; gap: 8mm; }
    h1 { font-size: 28px; font-weight: 700; margin: 0; text-align: center; }
    .qr-img { width: 200px; height: 200px; display: block; }
    .join-note { font-size: 14px; color: #333; text-align: center; margin: 0; }
    .warning { font-size: 13px; font-weight: 600; color: #d4380d; text-align: center; margin: 0; }
    .footer { width: 100%; max-width: 160mm; margin: 0 auto; padding: 6mm 0 0; border-top: 1px solid #eee; display: flex; flex-direction: column; align-items: center; gap: 4mm; }
    .footer img { height: 24px; opacity: 0.75; }
    .contact-info { width: 100%; text-align: center; }
    .contact-block { font-size: 12px; color: #666; margin: 2px 0; }
    .tag-url { font-size: 12px; color: #444; margin: 0; word-break: break-all; text-align: center; }
    .copyright { font-size: 11px; color: #aaa; margin: 0; }
  </style>
</head>
<body>
  <div class="page">
    <h1>${teamName || 'Team'}</h1>
    <img class="qr-img" src="${qrDataUrl}" alt="Invite QR code"/>
    <p class="join-note">Scan to join our team</p>
    <p class="warning">⚠️ Staff only — do not display this where customers can scan it.</p>
  </div>
  <div class="footer">
    ${contactInfoHtml}
    <p class="tag-url">${inviteUrl}</p>
    <img src="${logoSrc}" alt="striketip"/>
    <p class="copyright">© ${new Date().getFullYear()} powered by strikepay</p>
  </div>
  <script>window.onload = function(){ window.print(); }<\/script>
</body>
</html>`);
      printWindow.document.close();
    };

    return (
        <Modal title="Invite link"
            visible={visible === null ? true : visible}
            onOk={() => onClickInviteModalOk()}
            onCancel={() => onClickModalCancel()}
            footer={null}
            bodyStyle={{ minHeight: '200px' }}
        >
            <div style={{ textAlign: "center" }}>
                {!inviteUrl ? (
                    <p className="promoCopy" style={{ textAlign: "left" }}>
                        {canManageInvite
                            ? `Creating an invite link…`
                            : `This team doesn't have an invite link yet. Ask a team manager to create one.`}
                    </p>
                ) : (
                    <>
                        <p className="promoCopy" style={{ textAlign: "left" }}>{messageText}</p>

                        <div className="copyableBlock">
                            <span>Tap to copy link</span>

                            <CopyToClipboard
                                text= {inviteUrl}
                                onCopy={() => onCopied()}>
                                <button className="promoCopyable">{isLinkCopied ? 'Copy Again' : 'Copy Invite Link'}</button>
                            </CopyToClipboard>
                        </div>
                    </>
                )}

                {qrDataUrl && (
                  <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                    <img src={qrDataUrl} alt="Invite QR code" style={{ width: 200, height: 200 }} />
                    <p style={{ fontSize: 12, color: '#666', textAlign: 'center', maxWidth: 260, margin: 0 }}>
                      This QR code is for <strong>joining your team</strong> — not for tipping. Print it and display it in your staff area so team members can scan to join.
                    </p>
                    <p style={{ fontSize: 12, color: '#d4380d', fontWeight: 600, textAlign: 'center', maxWidth: 260, margin: 0 }}>
                      ⚠️ Staff only — do not display this where customers can scan it.
                    </p>
                    <Space>
                      <Button icon={<DownloadOutlined />} onClick={() => saveQRImage(qrDataUrl, teamName)}>
                        Share
                      </Button>
                      <Button icon={<FilePdfOutlined />} onClick={handlePrintQR}>
                        Print
                      </Button>
                    </Space>
                  </div>
                )}
            </div>
        </Modal>
    );
};
